import React, { useContext } from 'react'
import { useMutation } from 'react-apollo'
import { withRouter } from 'react-router-dom'

import { Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useFeature } from '@optimizely/react-sdk'
import * as Sentry from '@sentry/browser'
import { useFormik } from 'formikV2'

import SsnLockImage from 'assets/images/ssn-lock.svg'
import Button from 'components/Button'
import { CTAContainer } from 'components/CTAContainer'
import {
  FormItem,
  StyledFormContainer,
  StyledTextInput,
} from 'components/FormContainer/styled-components'
import withLiveContent from 'components/HOCs/WithLiveContent'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import { generateErrorCodeMap } from 'pages/SSN/errorCodeMap'
import { setupErrorModal } from 'pages/SSN/utils'
import { TestIDs } from 'test/utils/testIds'
import { withApolloErrorModal } from 'utils/Apollo'
import { withApolloErrorHandler } from 'utils/Apollo/apolloErrorHandlingProvider'
import Environment, { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { displayErrorModal, parseGraphQLError } from 'utils/form-utils'
import { FunnelContext } from 'utils/FunnelContext'
import { withOptimizely } from 'utils/Optimizely/'

import { getSchema } from './form'
import UpdatePersonalInfoMutation from './gql/mutation.gql'
import {
  FooterText,
  FormRow,
  ImageContainer,
  StyledHeroDescription,
  StyledHeroHeader,
  StyledHeroSubtext,
} from './styled-components'
import { FormValues, SsnProps, UpdatePersonalInfoInput, UpdatePersonalInfoResponse } from './types'

// Create a trackable Submit Personal Info button
export const SubmitSSNButton = withClickTrack<any, any>(TRACKER_IDS.SSN.NEXT_BUTTON)(Button)

const formatSsn = ssn => ssn.replace(/-/g, '')

const Ssn = (props: SsnProps, env: Environment) => {
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const [submitSsn, { loading }] = useMutation<UpdatePersonalInfoResponse, UpdatePersonalInfoInput>(
    UpdatePersonalInfoMutation,
  )
  const { getRemoteString } = useRemoteStrings()
  const [isSsnValidationDisabled] = useFeature('disable_ssn_validation')
  const schema = getSchema(isSsnValidationDisabled)

  const header = props.liveContent('hero_header', { remoteVariableKey: 'personal_info_header' })
  const text = props.liveContent('hero_text', { remoteVariableKey: 'personal_info_text' })
  const subText = props.liveContent('hero_subtext', { remoteVariableKey: 'personal_info_subtext' })
  const footnote = props.liveContent('hero_footnote')
  const footnoteLock = props.liveContent('hero_footnote_lock', { fallback: false, renderer: 'raw' })
  const submitCta = props.liveContent('submit_cta', { fallback: 'Next' })

  const {
    handleChange,
    values,
    submitForm,
    submitCount,
    validateForm,
    errors,
    isValid,
    isSubmitting,
    setSubmitting,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnMount: false,
    onSubmit: async values => {
      setSubmitting(true)
      Sentry.addBreadcrumb({ message: 'User attempting to submit personal info at /ssn' })
      submitSsn({ variables: { input: { ssn: formatSsn(values.ssn) } } })
        .then(() => {
          isDynamicFunnelEnabled
            ? routeToPage(PageName.Ssn, ActionType.Continue)
            : props.route({ from: FlowRoute.SSN, action: 'continue' })
        })
        .catch(error => {
          sendToSentry(error)
          setSubmitting(false)

          const errorCode = parseGraphQLError(error)
          displayErrorModal(
            props,
            errorCode,
            generateErrorCodeMap({ env, getString: getRemoteString, ...props }),
            {
              objectName: 'errorAlert',
              screen: 'registrationSsn',
            },
          )
        })
    },
    initialValues: { ssn: '' },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: schema,
  })

  const onChange = async e => {
    // https://github.com/jaredpalmer/formik/issues/1218#issuecomment-451119016
    const x = e.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,2})(\d{0,4})/)
    e.target.value = !x[2] ? x[1] : x[1] + '-' + x[2] + (x[3] ? '-' + x[3] : '')
    await handleChange(e)

    // Only validate the form if the user has attempted at least one submit
    if (submitCount > 0) {
      validateForm()
    }
  }

  return (
    <>
      <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.Ssn)} />
      <Layout header={header} text={text} footnoteLock={footnoteLock} footnote={footnote}>
        <ImageContainer>
          <img src={SsnLockImage} alt="ssn-lock" />
        </ImageContainer>
        <StyledHeroHeader>{text}</StyledHeroHeader>

        <StyledHeroDescription>
          <StyledHeroSubtext>{subText}</StyledHeroSubtext>
        </StyledHeroDescription>
        <StyledFormContainer>
          <FormRow>
            <FormItem>
              <StyledTextInput
                label={{
                  value: 'Social security number',
                }}
                className="text-input"
                inputProps={{
                  inputMode: 'numeric',
                  name: 'ssn',
                  value: values.ssn,
                  onChange,
                  id: 'ssn',
                  type: 'text',
                  placeholder: 'XXX-XX-XXXX',
                }}
                error={errors.ssn && submitCount > 0}
                errorText={submitCount > 0 ? (errors.ssn as string) : ''}
              />
            </FormItem>

            <FooterText>{footnote}</FooterText>
          </FormRow>
        </StyledFormContainer>
        <Separator className="mobile-cta-separator" />
        <CTAContainer>
          <SubmitSSNButton
            styleType={isValid && values.ssn ? undefined : 'invalid'}
            onPress={submitForm}
            data-testid={TestIDs.SSN.SUBMIT_CTA}
            disabled={isSubmitting || loading}
            buttonType={'submit'}
          >
            {isSubmitting || loading ? <Spinner height={20} width={20} white={true} /> : submitCta}
          </SubmitSSNButton>
        </CTAContainer>
      </Layout>
    </>
  )
}

const ApolloErrorHandler = withApolloErrorHandler(
  'updatePersonalInfo',
  ({ closeErrorModal }) => ({ graphQLErrors = [] }) => {
    return setupErrorModal(closeErrorModal, graphQLErrors)
  },
)

export default withApolloErrorModal(
  ApolloErrorHandler(withFlowRouter(withRouter(withEnv(withLiveContent(withOptimizely(Ssn)))))),
)
